/**
 * Iter XX.70 — VideoReviewCard
 *
 * Structured video-review card. Renders each insight as its own card:
 *  - Rank-1 (root cause) is always fully revealed.
 *  - Rank 2+ for free users show as LOCKED cards with real headline,
 *    category, severity, and a tap-to-upgrade CTA.
 *  - Plus users see every card fully.
 *
 * This component sits INSIDE a chat message bubble. It fetches the
 * gated payload from /api/video-review/{id} on mount and honours the
 * server-decided render_mode. When render_mode === "markdown_legacy",
 * the component renders nothing (Chat.jsx falls back to the message
 * text — the pre-XX.70 behaviour).
 *
 * Data-testids:
 *   video-review-card
 *   video-review-card-founder-preview-banner
 *   video-review-insight-card-{rank}
 *   video-review-insight-card-{rank}-locked  (only on locked cards)
 *   video-review-insight-title-{rank}
 *   video-review-insight-category-{rank}
 *   video-review-insight-severity-{rank}
 *   video-review-insight-body-{rank}         (only on unlocked cards)
 *   video-review-upgrade-cta                 (only when locked cards exist)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import LockedInsightCard from "./LockedInsightCard";
import VideoUpgradeSheet from "./VideoUpgradeSheet";

const SEVERITY_BADGE = {
  high:   { label: "PRIMARY",   className: "bg-emerald-900/40 text-emerald-300 border-emerald-800" },
  medium: { label: "SUPPORTING", className: "bg-amber-900/40 text-amber-300 border-amber-800" },
  low:    { label: "NOTE",      className: "bg-slate-800/60 text-slate-400 border-slate-700" },
};

export default function VideoReviewCard({
  videoAnalysisId,
  isFounderPreviewParam = false,
  onFallbackToMarkdown,
  onCardReady,
  autoReveal = false,
}) {
  const [state, setState] = useState({
    loading: true, error: null, data: null,
  });
  const [sheetOpen, setSheetOpen] = useState(false);
  const [revealedRanks, setRevealedRanks] = useState(new Set([1]));
  // Iter XX.70.1 — Deterministic reveal. The stagger MUST run only after
  // (a) the payload has returned, (b) every card has mounted, and (c)
  // the browser has painted one frame. We track this with an explicit
  // ref + rAF handshake so slow requests can never collapse the reveal
  // into an all-at-once flash. `staggerStartedRef` guards against
  // double-invocation from React 18 StrictMode + effect re-runs.
  const staggerStartedRef = useRef(false);
  const timeoutHandlesRef = useRef([]);
  const rafHandleRef = useRef(null);
  const cardsContainerRef = useRef(null);
  const isMountedRef = useRef(true);

  useEffect(() => () => {
    // Global cleanup on unmount: cancel any pending stagger timers +
    // rAF so nothing runs against an unmounted component.
    isMountedRef.current = false;
    timeoutHandlesRef.current.forEach((h) => clearTimeout(h));
    timeoutHandlesRef.current = [];
    if (rafHandleRef.current) cancelAnimationFrame(rafHandleRef.current);
  }, []);

  const fetchReveal = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const url = `/video-review/${encodeURIComponent(videoAnalysisId)}` +
        (isFounderPreviewParam ? "?founder_preview=1" : "");
      const { data } = await api.get(url);
      if (!isMountedRef.current) return;
      setState({ loading: false, error: null, data });
      if (data?.render_mode === "markdown_legacy" && onFallbackToMarkdown) {
        onFallbackToMarkdown();
      }
      if (onCardReady) onCardReady(data);

      const ranks = (data?.insights || []).map((i) => i.rank).filter(Boolean);
      if (!ranks.length) return;

      if (autoReveal && !staggerStartedRef.current) {
        // Reveal rank 1 immediately (it's always the root cause and
        // was already visible pre-payment); stagger the newly-unlocked
        // ranks after one paint tick so the DOM has settled.
        staggerStartedRef.current = true;
        setRevealedRanks(new Set([1]));
        rafHandleRef.current = requestAnimationFrame(() => {
          // A second rAF guarantees the browser has actually painted
          // the initial state before we start scheduling reveals — this
          // is the fix for the "cards collapse into one flash" bug when
          // the network response arrives faster than mount.
          rafHandleRef.current = requestAnimationFrame(() => {
            if (!isMountedRef.current) return;
            ranks.forEach((r, idx) => {
              if (r === 1) return;  // already revealed
              const staggerMs = (idx) * 700;
              const h = setTimeout(() => {
                if (!isMountedRef.current) return;
                setRevealedRanks((prev) => {
                  if (prev.has(r)) return prev;  // guard double-set
                  return new Set([...prev, r]);
                });
              }, staggerMs);
              timeoutHandlesRef.current.push(h);
            });
          });
        });
      } else {
        // No auto-reveal → show everything the server sent us right
        // away. Locked cards render as locked; unlocked as full.
        setRevealedRanks(new Set(ranks));
      }
    } catch (e) {
      if (!isMountedRef.current) return;
      setState({
        loading: false,
        data: null,
        error: (e?.response?.data?.detail || e?.message || "load_failed"),
      });
      if (onFallbackToMarkdown) onFallbackToMarkdown();
    }
  }, [videoAnalysisId, isFounderPreviewParam, onFallbackToMarkdown, onCardReady, autoReveal]);

  useEffect(() => { fetchReveal(); }, [fetchReveal]);

  const insights = useMemo(() => state.data?.insights || [], [state.data]);
  const lockedCount = state.data?.locked_count || 0;
  const founderPreview = !!state.data?.founder_preview;

  if (state.loading) {
    return (
      <div data-testid="video-review-card-loading"
           className="mt-2 text-xs text-slate-500">Preparing your review…</div>
    );
  }
  if (state.error || !state.data) return null;
  if (state.data.render_mode === "markdown_legacy") return null;

  return (
    <div
      data-testid="video-review-card"
      className="mt-2 space-y-3 rounded-2xl border border-slate-800 bg-slate-900/60 p-3 sm:p-4"
    >
      {founderPreview && (
        <div
          data-testid="video-review-card-founder-preview-banner"
          className="rounded-md border border-amber-700 bg-amber-950/40 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-amber-300"
        >
          Founder Preview · simulated free-tier view · billing untouched
        </div>
      )}

      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wide text-emerald-400">
          Gully Video Review · {state.data.kind || "clip"}
        </div>
        <div className="text-[11px] text-slate-500">
          {state.data.unlocked_count}/{insights.length} shown
        </div>
      </div>

      {insights.map((ins) => {
        const revealed = revealedRanks.has(ins.rank);
        if (!revealed) {
          return (
            <div
              key={ins.insight_id}
              data-testid={`video-review-insight-card-${ins.rank}-pending`}
              className="h-14 rounded-xl border border-slate-800 bg-slate-950/40 animate-pulse"
            />
          );
        }
        if (ins.locked) {
          return (
            <LockedInsightCard
              key={ins.insight_id}
              insight={ins}
              onClick={async () => {
                try {
                  await api.post(
                    `/video-review/${encodeURIComponent(videoAnalysisId)}/lock-clicked`,
                    { insight_id: ins.insight_id, surface: "locked_card" },
                  );
                } catch { /* telemetry never gates UX */ }
                setSheetOpen(true);
              }}
            />
          );
        }
        // Iter XX.70.1 — Rank-1 visual dominance. The root cause is the
        // "one genuinely valuable insight" free users are meant to
        // remember. Give it a subtly heavier treatment: emerald left-
        // border accent, more vertical breathing room, and clearer
        // separation between summary → why-it-matters → drill → cue.
        const isRootCause = ins.rank === 1;
        const cardClasses = isRootCause
          ? "rounded-xl border border-slate-800 border-l-2 border-l-emerald-500/70 bg-slate-950/60 p-4 sm:p-5 space-y-2"
          : "rounded-xl border border-slate-800 bg-slate-950/40 p-3 space-y-1";
        return (
          <div
            key={ins.insight_id}
            data-testid={`video-review-insight-card-${ins.rank}`}
            className={cardClasses}
          >
            <div className="flex items-center gap-2">
              <span
                data-testid={`video-review-insight-severity-${ins.rank}`}
                className={`inline-block rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-wider ${
                  (SEVERITY_BADGE[ins.severity] || SEVERITY_BADGE.medium).className
                }`}
              >
                {(SEVERITY_BADGE[ins.severity] || SEVERITY_BADGE.medium).label}
              </span>
              <span
                data-testid={`video-review-insight-category-${ins.rank}`}
                className="text-[11px] text-slate-400"
              >
                {ins.category}
              </span>
            </div>
            <div
              data-testid={`video-review-insight-title-${ins.rank}`}
              className={
                isRootCause
                  ? "text-[15px] sm:text-base font-semibold text-slate-50 leading-snug"
                  : "text-sm font-medium text-slate-100"
              }
            >
              {ins.title}
            </div>
            {ins.summary && (
              <div
                data-testid={`video-review-insight-body-${ins.rank}`}
                className={
                  isRootCause
                    ? "text-[14px] leading-relaxed text-slate-200"
                    : "text-[13px] leading-snug text-slate-300"
                }
              >
                {ins.summary}
              </div>
            )}
            {ins.explanation && (
              <div className={isRootCause ? "pt-1 text-[12px] text-slate-400" : "text-xs text-slate-500"}>
                <span className="text-slate-500">Why it matters — </span>
                {ins.explanation}
              </div>
            )}
            {ins.recommendation && (
              <div
                className={
                  isRootCause
                    ? "mt-2 rounded-md border border-slate-800 bg-slate-900/70 px-3 py-2 text-[13px] text-slate-200"
                    : "mt-1 rounded-md border border-slate-800 bg-slate-900/60 px-2.5 py-1.5 text-xs text-slate-300"
                }
              >
                <span className="text-slate-400">Try — </span>
                {ins.recommendation}
              </div>
            )}
            {ins.match_day_cue && (
              <div
                className={
                  isRootCause
                    ? "mt-2 text-[11px] uppercase tracking-widest text-emerald-400"
                    : "mt-1 text-[11px] uppercase tracking-wider text-emerald-400"
                }
              >
                Match-day cue → {ins.match_day_cue}
              </div>
            )}
          </div>
        );
      })}

      {lockedCount > 0 && (
        <button
          data-testid="video-review-upgrade-cta"
          onClick={async () => {
            try {
              await api.post(
                `/video-review/${encodeURIComponent(videoAnalysisId)}/lock-clicked`,
                { insight_id: "cta", surface: "footer_cta" },
              );
            } catch { /* swallow */ }
            setSheetOpen(true);
          }}
          className="w-full rounded-xl bg-emerald-600 py-2.5 text-sm font-medium text-white hover:bg-emerald-500 transition-colors"
        >
          Continue this review with Gully Plus
        </button>
      )}

      <div ref={cardsContainerRef} />

      {sheetOpen && (
        <VideoUpgradeSheet
          videoAnalysisId={videoAnalysisId}
          founderPreview={founderPreview}
          onDismiss={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}
